import { detectArrayChanges } from '@trimble/utilities';
import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { MeasurementsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, createEffect, createRoot, on } from 'solid-js';
import { Web3dTool } from '@trimble/web3d-providers';
import { SharedCapabilityBase } from '../base/shared-capability-base';
import { CapabilityCategory } from '../base/models/capability-category';
import { Tool, Web3DViewer } from '@technology/web3d';
import { PaneToolGate } from '../../helpers/pane-tool-gate';

export abstract class SimpleMeasurementCapabilityBase extends SharedCapabilityBase {
  protected abstract measurementType: Web3dTool;
  public override category: CapabilityCategory = CapabilityCategory.Markup;
  public readonly hasMeasurements: Accessor<boolean>;
  protected effectDispose?: () => void;
  protected readonly toolGate: PaneToolGate;

  constructor(
    protected readonly measurementsProvider: MeasurementsCapabilityProvider
  ) {
    super(measurementsProvider);

    this.hasMeasurements = this.measurementsProvider.hasItems;
    // Another tool may have taken over the viewer while the gate held this one off.
    this.toolGate = new PaneToolGate(
      this.viewerWrappers,
      (viewer, tool) => viewer.activeTool === tool.name
    );
  }

  protected measurementTool(viewer: Web3DViewer): Tool | undefined {
    return viewer.tools[this.measurementType] as Tool | undefined;
  }

  protected gateMeasurementTool(viewer: Web3DViewer): void {
    const tool = this.measurementTool(viewer);
    if (tool) {
      this.toolGate.request(viewer, tool);
    }
  }

  protected releaseMeasurementTool(viewer: Web3DViewer): void {
    const tool = this.measurementTool(viewer);
    if (tool) {
      this.toolGate.release(tool);
    }
  }

  public startMeasurement(): Promise<void> {
    this.effectDispose?.();
    this.effectDispose = createRoot((dispose) => {
      createEffect(
        on(this.viewerWrappers, (viewerWrappers, prevViewerWrappers) => {
          const newViewerWrappers = detectArrayChanges(
            prevViewerWrappers,
            viewerWrappers
          ).newItems;
          if (!newViewerWrappers.length) {
            return;
          }
          this.activate();
          this.setupWeb3dMeasurementTool(newViewerWrappers);
        })
      );
      return dispose;
    });

    return Promise.resolve();
  }

  protected async setupWeb3dMeasurementTool(
    viewerWrappers: Web3DViewerWrapper[]
  ): Promise<void> {
    for (const viewerWrapper of viewerWrappers) {
      viewerWrapper.viewer.activeTool = this.measurementType;
      this.gateMeasurementTool(viewerWrapper.viewer);
    }
    return Promise.resolve();
  }

  public stopMeasurement(): Promise<void> {
    this.deactivate();
    return Promise.resolve();
  }

  public override deactivate(): boolean {
    this.effectDispose?.();
    this.effectDispose = undefined;
    for (const viewerWrapper of this.viewerWrappers()) {
      // web3d markup tools ignore cancel() while disabled, and cancel() is what restores the default tool.
      this.releaseMeasurementTool(viewerWrapper.viewer);
      // Only cancel if this capability's tool is still active
      // Prevents overwriting another measurement that activated before this deactivate ran
      if (viewerWrapper.viewer.activeTool === this.measurementType) {
        this.measurementTool(viewerWrapper.viewer)?.cancel();
      }
    }
    return super.deactivate();
  }

  public removeAllMeasurements(): void {
    this.measurementsProvider.removeAllMeasurements();
  }

  public override trackIsEnabled(): void {
    // Maybe we should track if any measurable objects are loaded in viewer?
    this.enable();
  }

  public override cleanup(): Promise<void> {
    this.effectDispose?.();
    this.effectDispose = undefined;
    this.toolGate.dispose();
    return Promise.resolve();
  }
}
